import { faFeatherPointed, faRobot } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import DiscoveryBadge from '@/components/InsuranceSection/DiscoveryBadge';
import AppContext from '@/contexts/appContext';
import PersonContext from '@/pages/Person/PersonContext';
import CopyContent from '@/ui/components/CopyContent';
import DataList, { DataRow } from '@/ui/components/DataList';
import Heading from '@/ui/components/Heading';
import IconItem from '@/ui/components/IconItem';
import ImageTooltip from '@/ui/components/ImageTooltip';
import Tooltip from '@/ui/components/Tooltip';
import formatStaffName from '@/utils/formatStaffName';
import { hasPermission, PEOPLE_RUN_VOB } from '@/utils/userPermission';

import { getInsuranceFinanceFormatted } from '../utils';

import type { Props } from './types';
import { getInsuranceIconAndColor } from './utils';

const InsuranceSummary = ({ insurancePolicy }: Props) => {
  const { permissions } = useContext(AppContext);
  const { person } = useContext(PersonContext);
  const navigate = useNavigate();
  const [isReverified] = useState(false);

  const {
    id: policyId,
    carrier,
    type,
    priority_type,
    co_pay,
    co_insurance,
    co_pay_unit,
    last_verification,
    plan_end_date: plan_end_date_str,
    treatability_status,
    subscriber_id,
    provider_name,
    insurance_card_images,
    was_discovered,
    is_self_pay
  } = insurancePolicy;

  const {
    deductible_remaining,
    status: verifiedStatus,
    verified_at,
    verified_by,
    verified_via,
    out_of_pocket_maximum_remaining_in_cents
  } = last_verification || {};

  const canRunVob = useMemo(() => {
    const requiredPermission = PEOPLE_RUN_VOB;
    return hasPermission({ permissions, requiredPermission });
  }, [permissions]);

  const handleReverify = () => {
    if (!policyId) return;
    navigate(`${policyId}/auto-reverify`, {
      state: {
        insurancePolicy
      }
    });
  };

  const coPay = getInsuranceFinanceFormatted({
    currencyToFormat: co_pay,
    suffix: co_pay_unit ? `x ${co_pay_unit}` : ''
  });

  const oopMaxRemaining = getInsuranceFinanceFormatted({
    currencyToFormat: out_of_pocket_maximum_remaining_in_cents
  });

  const coInsurance = co_insurance ? `${co_insurance * 100}%` : null;

  const deductibleRemaining = getInsuranceFinanceFormatted({
    currencyToFormat: deductible_remaining
  });

  const verifiedBy = verified_by ? `, by ${formatStaffName({ staff: verified_by })}` : '  ';
  const verifiedAt = verified_at
    ? `Verified on ${new Date(verified_at).toLocaleDateString()}`
    : null;

  const verifiedVia = useMemo(() => {
    if (typeof verified_via === 'string') {
      return `, via ${verified_via}`;
    }

    return `, via ${verified_via?.name || 'No Source'}`;
  }, [verified_via]);

  const { icon, color } = getInsuranceIconAndColor(treatability_status, plan_end_date_str);

  const manuallyEnteredByUserTooltip = (
    <Tooltip tooltipContent="Manually Entered by user">
      <span data-testid="manually-entered-icon" className="ml-1 text-yellow-700">
        <FontAwesomeIcon icon={faFeatherPointed} />
      </span>
    </Tooltip>
  );

  const frontImageTooltip = (
    <ImageTooltip
      imageUrl={insurance_card_images?.front_image_url ?? undefined}
      label="Front Image"
    />
  );
  const backImageTooltip = (
    <ImageTooltip
      imageUrl={insurance_card_images?.back_image_url ?? undefined}
      label="Back Image"
    />
  );

  const renderAutomaticReverification = () => {
    if (!canRunVob || is_self_pay) return null;

    if (isReverified) {
      return (
        <Tooltip tooltipContent="Refresh the page to see updated results">
          <span className="ml-4 text-gray-300 cursor-not-allowed">
            <FontAwesomeIcon icon={faRobot} />
          </span>
        </Tooltip>
      );
    }

    return (
      <Tooltip tooltipContent="Run an automatic reverification❗ This creates both an External Policy Verification AND an Insurance Verification record for this policy">
        <button
          className="ml-4 text-gray-400 hover:text-gray-600 focus:outline-hidden"
          onClick={() => {
            handleReverify();
          }}
          aria-label="Automatic Reverification"
          data-testid="auto-reverify-button">
          <FontAwesomeIcon icon={faRobot} />
        </button>
      </Tooltip>
    );
  };

  return (
    <IconItem key={policyId} color={color} icon={icon}>
      <span className="flex items-center justify-normal gap-2">
        <Heading value={3} text={carrier ?? '-'}>
          {!provider_name ? manuallyEnteredByUserTooltip : null}
        </Heading>
        {was_discovered && person.most_recent_insurance_discovery && (
          <DiscoveryBadge
            requestedAt={person.most_recent_insurance_discovery.requested_at}
            source={person.most_recent_insurance_discovery.source}
          />
        )}
        {renderAutomaticReverification()}
      </span>
      <p className="text-cyan-700 font-semibold text-sm">
        {is_self_pay
          ? 'Verification not applicable: self pay'
          : verifiedStatus
            ? `${verifiedAt}${verifiedBy}${verifiedVia}`
            : 'Pending Verification'}
      </p>
      <DataList className="[&>div]:flex [&>div]:flex-row [&>div]:gap-2">
        <DataRow label="Subscriber ID:" sensitive>
          {subscriber_id ? <CopyContent content={subscriber_id} /> : '-'}
        </DataRow>
        <DataRow label="Type:">{type ?? '-'}</DataRow>
        <DataRow label="Order:">{priority_type ?? '-'}</DataRow>
        <DataRow label="Co-pay:">{coPay ?? '-'}</DataRow>
        <DataRow label="Co-insurance:">{coInsurance ?? '-'}</DataRow>
        <DataRow label="OOP Max remaining:">{oopMaxRemaining ?? '-'}</DataRow>
        <DataRow label="Deductible remaining:">{deductibleRemaining ?? '-'}</DataRow>
        <DataRow label="Insurance Card:">
          {frontImageTooltip} | {backImageTooltip}
        </DataRow>
      </DataList>
    </IconItem>
  );
};

export default React.memo(InsuranceSummary);
